import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Table,
  Button,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Divider,
  Tabs,
  Alert,
  Popconfirm,
  Radio,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  ScheduleOutlined,
  PercentageOutlined,
  CalculatorOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import { TourContext } from "../context/TourContext";
import LoanProductCalculator from "../components/loanProductCalculator";
import { normalizeTermOptions } from "../utils/loanUtils";

const { Title, Text } = Typography;
const { Option } = Select;

const LoanProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [selectedReloaningOption, setSelectedReloaningOption] = useState("");
  const { company } = useContext(AppContext);
  const { runTour, tourContent, startTour, endTour } = useContext(TourContext);
  const token = localStorage.getItem("token");

  const INTEREST_TYPES = [
    { label: "Simple Interest", value: "simple" },
    { label: "Compound Interest", value: "compound" },
  ];

  const TERM_UNITS = [
    { label: "Days", value: "days" },
    { label: "Weeks", value: "weeks" },
    { label: "Months", value: "months" },
    { label: "Years", value: "years" },
  ];

  const PAYMENT_FREQUENCIES = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
    { label: "Bi-Weekly", value: "bi_weekly" },
    { label: "3 Weeks", value: "3_weeks" },
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `https://zedlenders.pickmesms.com/items/loan_products?filter[company]=${company.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setProducts(res.data.data);
    } catch (error) {
      message.error("Failed to fetch loan products");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Validate that all required fields across tabs are filled
      await form.validateFields();

      const termOptions = normalizeTermOptions(values.loan_term_options);
      const allowMultipleTerms = values.allow_multiple_terms;
      const {
        allow_multiple_terms: _allowMultipleTerms,
        loan_term_options: _loanTermOptions,
        ...restValues
      } = values;

      if (values.auto_split_payments && values.payment_frequency !== "monthly") {
        message.error(
          "Auto-split payments currently supports monthly payment frequency only."
        );
        return;
      }

      const url = currentProduct
        ? `https://zedlenders.pickmesms.com/items/loan_products/${currentProduct.id}`
        : "https://zedlenders.pickmesms.com/items/loan_products";

      const method = currentProduct ? "patch" : "post";

      await axios[method](
        url,
        {
          ...restValues,
          company: company.id,
          status: "published",
          loan_term_options:
            allowMultipleTerms && termOptions.length > 0 ? termOptions : null,
          loan_term:
            allowMultipleTerms && termOptions.length > 0
              ? termOptions[0]
              : values.loan_term,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      message.success(
        `Product ${currentProduct ? "updated" : "created"} successfully`
      );
      setShowCreateModal(false);
      fetchProducts();
      form.resetFields();
    } catch (error) {
      if (error.errorFields) {
        message.error("Please complete all required fields in all tabs.");
      } else {
        message.error("Operation failed. Please try again.");
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      // First check if there are any loans using this product
      const loansResponse = await axios.get(
        `https://zedlenders.pickmesms.com/items/loans?filter[loan_product][_eq]=${id}&fields=id`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const existingLoans = loansResponse.data.data || [];

      if (existingLoans.length > 0) {
        setDeleteError(
          `Cannot delete this loan product. There are ${existingLoans.length} existing loan(s) using this product. Please reassign or close those loans first.`
        );
        return;
      }

      // If no loans are using this product, proceed with deletion
      await axios.delete(
        `https://zedlenders.pickmesms.com/items/loan_products/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeleteError(""); // Clear any previous error
      message.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      if (error.response?.status === 404) {
        message.error("Loan product not found");
      } else if (error.response?.data?.errors?.[0]?.message) {
        message.error(error.response.data.errors[0].message);
      } else {
        message.error("Delete operation failed. Please try again.");
      }
    }
  };

  const columns = [
    {
      title: "Product Name",
      dataIndex: "loan_name",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">{record.loan_description}</Text>
        </div>
      ),
    },
    {
      title: "Interest",
      render: (_, record) => (
        <>
          <Tag icon={<PercentageOutlined />} color="blue">
            {record.interest_rate}% {record.interest_type}
          </Tag>
          {record.compound_frequency && record.interest_type === "compound" && (
            <Tag color="geekblue">{record.compound_frequency}</Tag>
          )}
        </>
      ),
    },
    {
      title: "Term & Amount",
      width: 180,
      render: (_, record) => (
        <div>
          <Text strong>
            {(() => {
              const termOptions = normalizeTermOptions(
                record.loan_term_options
              );
              if (termOptions.length > 0) {
                return `${termOptions.join(", ")} ${record.term_unit}`;
              }
              return `${record.loan_term} ${record.term_unit}`;
            })()}
          </Text>
          <br />
          <Text>
            ZMW {record.min_amount?.toLocaleString()} -{" "}
            {record.max_amount?.toLocaleString()}
          </Text>
        </div>
      ),
    },
    {
      title: "Fees",
      render: (_, record) => (
        <div>
          {record.processing_fee_type !== "none" && (
            <Text>
              {record.processing_fee_type === "percentage"
                ? `${record.processing_fee}%`
                : `ZMW ${record.processing_fee}`}
            </Text>
          )}
          {record.late_payment_fee && (
            <Text type="secondary" style={{ display: "block" }}>
              Late fee: ZMW {record.late_payment_fee}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (active, record) => (
        <div>
          <Tag color={active ? "green" : "orange"}>
            {active ? "Active" : "Archived"}
          </Tag>
          {record.reloaning_settings?.enabled && (
            <Tag color="blue" style={{ marginTop: 4 }}>
              Auto-Reloaning
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              const termOptions = normalizeTermOptions(
                record.loan_term_options
              );
              setCurrentProduct(record);
              form.setFieldsValue({
                ...record,
                allow_multiple_terms: termOptions.length > 0,
                loan_term_options: termOptions,
              });
              // Set the selected reloaning option for conditional rendering
              if (record.reloaning_settings?.option) {
                setSelectedReloaningOption(record.reloaning_settings.option);
              }
              setShowCreateModal(true);
            }}
          />
          <Popconfirm
            title="Delete this product?"
            description="This will check if any loans exist before deletion."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes, Delete"
            cancelText="Cancel"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Tour Modal */}
      {runTour && tourContent && (
        <Modal
          title={tourContent.title || "Loan Products Guide"}
          open={runTour}
          onCancel={() => endTour(false)}
          footer={[
            <Button key="skip" onClick={() => endTour(false)}>
              Skip
            </Button>,
            <Button key="next" type="primary" onClick={() => endTour(true)}>
              Got it!
            </Button>,
          ]}
          width={500}
        >
          <div style={{ padding: "10px 0" }}>
            <Alert
              message={tourContent.message || "Welcome to Loan Products!"}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Text>Tips for creating loan products:</Text>
            <ul>
              {tourContent.details?.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          </div>
        </Modal>
      )}

      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title
              level={3}
              className="loan-products-title"
              style={{ marginBottom: 0 }}
            >
              Loan Product Management
            </Title>
            <Text type="secondary">
              Manage your lending products and configurations
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              className="new-product-button"
              icon={<PlusOutlined />}
              onClick={() => {
                setCurrentProduct(null);
                form.resetFields();
                setSelectedReloaningOption("");
                setShowCreateModal(true);
              }}
            >
              New Product
            </Button>
          </Col>
        </Row>

        {/* Key Metrics */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Active Products"
                value={products.filter((p) => p.is_active).length}
                prefix={<DollarOutlined />}
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Avg Interest Rate"
                value={(
                  products.reduce((sum, p) => sum + p.interest_rate, 0) /
                  products.length
                ).toFixed(1)}
                suffix="%"
                prefix={<PercentageOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Total Loan Capacity"
                value={products.reduce((sum, p) => sum + p.max_amount, 0)}
                prefix="ZMW"
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card bordered={false}>
              <Statistic
                title="Processing/Risk Fees"
                value={(() => {
                  const productsWithFees = products.filter(
                    (p) => p.processing_fee_type !== "none"
                  );
                  if (productsWithFees.length === 0) {
                    return "No fees set";
                  }

                  const percentageFees = products.filter(
                    (p) => p.processing_fee_type === "percentage"
                  );
                  const fixedFees = products.filter(
                    (p) => p.processing_fee_type === "fixed"
                  );

                  if (percentageFees.length > 0 && fixedFees.length > 0) {
                    return "Mixed types";
                  } else if (percentageFees.length > 0) {
                    const avgPercentage =
                      percentageFees.reduce(
                        (sum, p) => sum + p.processing_fee,
                        0
                      ) / percentageFees.length;
                    return `${avgPercentage.toFixed(1)}%`;
                  } else if (fixedFees.length > 0) {
                    const avgFixed =
                      fixedFees.reduce((sum, p) => sum + p.processing_fee, 0) /
                      fixedFees.length;
                    return `ZMW ${avgFixed.toFixed(0)}`;
                  }

                  return "No fees set";
                })()}
                prefix={<CalculatorOutlined />}
                valueStyle={{ color: "#13c2c2" }}
              />
            </Card>
          </Col>
        </Row>

        {deleteError && (
          <Alert
            message="Cannot Delete Loan Product"
            description={deleteError}
            type="error"
            showIcon
            closable
            onClose={() => setDeleteError("")}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          className="loan-products-table"
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ margin: 0 }}>
                <Tabs size="small">
                  <Tabs.TabPane tab="Eligibility" key="eligibility">
                    {record.eligibility_criteria ? (
                      <Row gutter={16}>
                        <Col span={12}>
                          <Text strong>Minimum Credit Score:</Text>{" "}
                          {record.eligibility_criteria.min_credit_score ||
                            "N/A"}
                        </Col>
                        <Col span={12}>
                          <Text strong>Employment Type:</Text>{" "}
                          {record.eligibility_criteria.employment_type || "Any"}
                        </Col>
                      </Row>
                    ) : (
                      <Text type="secondary">
                        No specific eligibility criteria
                      </Text>
                    )}
                  </Tabs.TabPane>
                  <Tabs.TabPane tab="Advanced Settings" key="advanced">
                    <Row gutter={16}>
                      <Col span={8}>
                        <Text strong>Grace Period:</Text> {record.grace_period}{" "}
                        days
                      </Col>
                      <Col span={8}>
                        <Text strong>Collateral Required:</Text>{" "}
                        {record.requires_collateral ? "Yes" : "No"}
                      </Col>
                      <Col span={8}>
                        <Text strong>Currency:</Text> {record.currency}
                      </Col>
                    </Row>
                    {record.reloaning_settings?.enabled && (
                      <>
                        <Divider
                          orientation="left"
                          style={{ margin: "16px 0 8px 0" }}
                        >
                          Reloaning Configuration
                        </Divider>
                        <Row gutter={16} style={{ marginBottom: 8 }}>
                          <Col span={8}>
                            <Text strong>Option:</Text>{" "}
                            {record.reloaning_settings.option || "N/A"}
                          </Col>
                          <Col span={8}>
                            <Text strong>Min Days Overdue:</Text>{" "}
                            {record.reloaning_settings.min_days_overdue ||
                              "N/A"}
                          </Col>
                          <Col span={8}>
                            <Text strong>Max Attempts:</Text>{" "}
                            {record.reloaning_settings.max_attempts || "N/A"}
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col span={8}>
                            <Text strong>Auto-approve:</Text>{" "}
                            {record.reloaning_settings.auto_approve
                              ? "Yes"
                              : "No"}
                          </Col>
                          <Col span={8}>
                            <Text strong>Manual Review:</Text>{" "}
                            {record.reloaning_settings.require_manual_review
                              ? "Yes"
                              : "No"}
                          </Col>
                          <Col span={8}>
                            <Text strong>Max Outstanding:</Text>{" "}
                            {
                              record.reloaning_settings
                                .max_outstanding_percentage
                            }
                            %
                          </Col>
                        </Row>
                      </>
                    )}
                  </Tabs.TabPane>
                </Tabs>
              </div>
            ),
          }}
        />
      </Card>

      {/* Product Creation/Edit Modal */}
      <Modal
        title={`${currentProduct ? "Edit" : "New"} Loan Product`}
        open={showCreateModal}
        onCancel={() => {
          setShowCreateModal(false);
          form.resetFields();
          setCurrentProduct(null);
          setSelectedReloaningOption("");
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            currency: "ZMW",
            processing_fee_type: "none",
            interest_type: "simple",
            allow_multiple_terms: false,
            auto_split_payments: false,
          }}
        >
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="Basic Info" key="1">
              <Form.Item
                label="Product Name"
                name="loan_name"
                rules={[{ required: true }]}
              >
                <Input placeholder="e.g., 30-Day Business Loan" />
              </Form.Item>

              <Form.Item label="Description" name="loan_description">
                <Input.TextArea rows={3} />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Minimum Amount (ZMW)"
                    name="min_amount"
                    rules={[{ required: true }]}
                  >
                    <InputNumber style={{ width: "100%" }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Maximum Amount (ZMW)"
                    name="max_amount"
                    rules={[{ required: true }]}
                  >
                    <InputNumber style={{ width: "100%" }} min={0} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Multiple Tenures"
                    name="allow_multiple_terms"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Multiple" unCheckedChildren="Single" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Term Unit"
                    name="term_unit"
                    rules={[{ required: true }]}
                  >
                    <Select options={TERM_UNITS} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                shouldUpdate
                dependencies={["allow_multiple_terms"]}
                noStyle
              >
                {({ getFieldValue }) => {
                  const allowMultiple = getFieldValue("allow_multiple_terms");
                  return allowMultiple ? (
                    <Form.Item
                      label="Term Options"
                      name="loan_term_options"
                      rules={[
                        {
                          required: true,
                          message: "Enter at least one term option",
                        },
                      ]}
                      extra="Enter terms as numbers (e.g., 1, 3, 6)."
                    >
                      <Select
                        mode="tags"
                        tokenSeparators={[",", " "]}
                        placeholder="e.g., 1, 3, 6"
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      label="Loan Term"
                      name="loan_term"
                      rules={[{ required: true }]}
                    >
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Interest & Fees" key="2">
              <Form.Item
                label="Interest Type"
                name="interest_type"
                rules={[{ required: true }]}
              >
                <Select options={INTEREST_TYPES} />
              </Form.Item>

              <Form.Item
                label="Allow Custom Interest Rate Per Loan"
                name="allow_custom_interest_rate"
                valuePropName="checked"
                extra="Enable loan officers to set custom interest rates within min/max range"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.allow_custom_interest_rate !== currentValues.allow_custom_interest_rate
                }
              >
                {({ getFieldValue, setFieldsValue }) => {
                  const allowCustomRate = getFieldValue("allow_custom_interest_rate");

                  // When custom rates are enabled, set interest_rate to 0
                  if (allowCustomRate && getFieldValue("interest_rate") !== 0) {
                    setFieldsValue({ interest_rate: 0 });
                  }

                  return allowCustomRate ? (
                    <>
                      <Alert
                        message="Custom Interest Rate Mode"
                        description="Each loan created with this product will require a custom interest rate to be set within the min/max range below. The default rate field is disabled."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label="Min Interest Rate (%)"
                            name="interest_rate_min"
                            rules={[{ required: true, message: "Required when custom rates enabled" }]}
                          >
                            <InputNumber
                              min={0}
                              max={500}
                              style={{ width: "100%" }}
                              formatter={(value) => `${value}%`}
                              parser={(value) => value.replace('%', '')}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Max Interest Rate (%)"
                            name="interest_rate_max"
                            rules={[{ required: true, message: "Required when custom rates enabled" }]}
                          >
                            <InputNumber
                              min={0}
                              max={500}
                              style={{ width: "100%" }}
                              formatter={(value) => `${value}%`}
                              parser={(value) => value.replace('%', '')}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      {/* Hidden field to store 0 as default rate when custom rates enabled */}
                      <Form.Item
                        name="interest_rate"
                        hidden
                        initialValue={0}
                      >
                        <InputNumber />
                      </Form.Item>
                    </>
                  ) : (
                    <Form.Item
                      label="Interest Rate (%)"
                      name="interest_rate"
                      rules={[{ required: true }]}
                    >
                      <InputNumber
                        min={0}
                        max={500}
                        style={{ width: "100%" }}
                        formatter={(value) => `${value}%`}
                        parser={(value) => value.replace('%', '')}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item
                label="Payment Frequency"
                name="payment_frequency"
                rules={[{ required: true }]}
              >
                <Select options={PAYMENT_FREQUENCIES} />
              </Form.Item>

              <Form.Item
                label="Auto-Split Payments"
                name="auto_split_payments"
                valuePropName="checked"
                extra="Automatically generate equal monthly installments for this product."
              >
                <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
              </Form.Item>

              <Form.Item
                label="Processing/Risk Fee Type"
                name="processing_fee_type"
                rules={[{ required: true }]}
              >
                <Select
                  onChange={(value) => {
                    if (value === "none") {
                      form.setFieldValue("processing_fee", undefined);
                    } else if (!form.getFieldValue("processing_fee")) {
                      // Set a default value when switching from 'none' to a fee type
                      form.setFieldValue(
                        "processing_fee",
                        value === "percentage" ? 5 : 100
                      );
                    }
                  }}
                >
                  <Option value="none">None</Option>
                  <Option value="percentage">Percentage</Option>
                  <Option value="fixed">Fixed Amount</Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Processing Fee"
                name="processing_fee"
                dependencies={["processing_fee_type"]}
                rules={[
                  ({ getFieldValue }) => ({
                    required: getFieldValue("processing_fee_type") !== "none",
                    message:
                      "Processing fee is required when type is not 'None'",
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder={
                    form.getFieldValue("processing_fee_type") === "percentage"
                      ? "Enter percentage (e.g., 5 for 5%)"
                      : "Enter amount in ZMW"
                  }
                  formatter={(value) =>
                    form.getFieldValue("processing_fee_type") === "percentage"
                      ? `${value}%`
                      : `ZMW ${value}`
                  }
                  parser={(value) => value.replace(/[^\d.]/g, "")}
                />
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Advanced" key="3">
              <Form.Item label="Grace Period (days)" name="grace_period">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Requires Collateral"
                name="requires_collateral"
                valuePropName="checked"
                rules={[
                  {
                    required: true,
                    message: "Please specify if collateral is required",
                  },
                ]}
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label="Status"
                name="is_active"
                valuePropName="checked"
                rules={[{ required: true, message: "Please specify the status" }]}
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Archived"
                />
              </Form.Item>

              <Form.Item
                label="Eligibility Criteria"
                name="eligibility_criteria"
              >
                <Input.Group>
                  <Form.Item
                    name={["eligibility_criteria", "min_credit_score"]}
                  >
                    <InputNumber
                      placeholder="Min Credit Score"
                      min={300}
                      max={850}
                      style={{ width: "100%", marginBottom: 8 }}
                    />
                  </Form.Item>
                  <Form.Item name={["eligibility_criteria", "employment_type"]}>
                    <Select
                      placeholder="Employment Type"
                      options={[
                        { label: "Formal", value: "formal" },
                        { label: "Informal", value: "informal" },
                        { label: "Self-Employed", value: "self_employed" },
                      ]}
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>

              <Divider orientation="left">Automated Reloaning Settings</Divider>

              <Form.Item
                label="Enable Automated Reloaning"
                name={["reloaning_settings", "enabled"]}
                valuePropName="checked"
                extra="Allow automatic refinancing or top-up loans for defaulted borrowers"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                dependencies={[["reloaning_settings", "enabled"]]}
                style={{ marginBottom: 0 }}
              >
                {({ getFieldValue }) => {
                  const reloningEnabled = getFieldValue([
                    "reloaning_settings",
                    "enabled",
                  ]);
                  return reloningEnabled ? (
                    <>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label="Minimum Days Overdue"
                            name={["reloaning_settings", "min_days_overdue"]}
                            rules={[
                              {
                                required: true,
                                message: "Required when reloaning is enabled",
                              },
                            ]}
                            extra="Minimum days loan must be overdue before reloaning eligibility"
                          >
                            <InputNumber
                              min={1}
                              style={{ width: "100%" }}
                              placeholder="e.g., 30"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Maximum Outstanding Balance (%)"
                            name={[
                              "reloaning_settings",
                              "max_outstanding_percentage",
                            ]}
                            rules={[
                              {
                                required: true,
                                message: "Required when reloaning is enabled",
                              },
                            ]}
                            extra="Max % of current amount owed that can be reloaned"
                          >
                            <InputNumber
                              min={1}
                              max={100}
                              style={{ width: "100%" }}
                              placeholder="e.g., 80"
                              formatter={(value) => `${value}%`}
                              parser={(value) => value.replace("%", "")}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        label="Reloaning Option"
                        name={["reloaning_settings", "option"]}
                        rules={[
                          {
                            required: true,
                            message: "Select a reloaning option",
                          },
                        ]}
                      >
                        <Radio.Group
                          onChange={(e) =>
                            setSelectedReloaningOption(e.target.value)
                          }
                          value={selectedReloaningOption}
                        >
                          <Radio value="refinance">
                            Refinance (Restructure existing loan)
                          </Radio>
                          <Radio value="topup">
                            Top-up (Add additional amount)
                          </Radio>
                          <Radio value="rollover">
                            Rollover (Extend term with penalty)
                          </Radio>
                        </Radio.Group>
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label="Max Reloaning Attempts"
                            name={["reloaning_settings", "max_attempts"]}
                            rules={[
                              {
                                required: true,
                                message: "Required when reloaning is enabled",
                              },
                            ]}
                            extra="Maximum number of times a borrower can reloan"
                          >
                            <InputNumber
                              min={1}
                              max={5}
                              style={{ width: "100%" }}
                              placeholder="e.g., 2"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Credit Score Threshold"
                            name={[
                              "reloaning_settings",
                              "min_credit_score_reloan",
                            ]}
                            extra="Minimum credit score required for reloaning eligibility"
                          >
                            <InputNumber
                              min={300}
                              max={850}
                              style={{ width: "100%" }}
                              placeholder="e.g., 600"
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {selectedReloaningOption === "refinance" && (
                        <>
                          <Divider orientation="left" orientationMargin="0">
                            Refinance Settings
                          </Divider>

                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item
                                label="Interest Rate Adjustment (%)"
                                name={[
                                  "reloaning_settings",
                                  "refinance_rate_adjustment",
                                ]}
                                extra="Additional interest rate for refinanced loans"
                              >
                                <InputNumber
                                  min={0}
                                  max={50}
                                  style={{ width: "100%" }}
                                  placeholder="e.g., 2"
                                  formatter={(value) => `+${value}%`}
                                  parser={(value) =>
                                    value.replace("+", "").replace("%", "")
                                  }
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Term Extension Options"
                                name={[
                                  "reloaning_settings",
                                  "term_extension_options",
                                ]}
                                extra="Available term extensions (in months)"
                              >
                                <Select
                                  mode="multiple"
                                  placeholder="Select term extensions"
                                  options={[
                                    { label: "1 month", value: 1 },
                                    { label: "2 months", value: 2 },
                                    { label: "3 months", value: 3 },
                                    { label: "4 months", value: 4 },
                                    { label: "5 months", value: 5 },
                                    { label: "6 months", value: 6 },
                                    { label: "12 months", value: 12 },
                                    { label: "18 months", value: 18 },
                                    { label: "24 months", value: 24 },
                                  ]}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )}

                      {selectedReloaningOption === "topup" && (
                        <>
                          <Divider orientation="left" orientationMargin="0">
                            Top-up Settings
                          </Divider>

                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item
                                label="Min Top-up Amount (ZMW)"
                                name={[
                                  "reloaning_settings",
                                  "topup_min_amount",
                                ]}
                                extra="Minimum additional amount for top-up loans"
                              >
                                <InputNumber
                                  min={0}
                                  style={{ width: "100%" }}
                                  placeholder="e.g., 1000"
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Max Top-up Amount (ZMW)"
                                name={[
                                  "reloaning_settings",
                                  "topup_max_amount",
                                ]}
                                extra="Maximum additional amount for top-up loans"
                              >
                                <InputNumber
                                  min={0}
                                  style={{ width: "100%" }}
                                  placeholder="e.g., 50000"
                                />
                              </Form.Item>
                            </Col>
                          </Row>

                          <Form.Item
                            label="Top-up Processing Fee (%)"
                            name={[
                              "reloaning_settings",
                              "topup_processing_fee",
                            ]}
                            extra="Additional processing fee for top-up loans"
                          >
                            <InputNumber
                              min={0}
                              max={10}
                              style={{ width: "100%" }}
                              placeholder="e.g., 1.5"
                              formatter={(value) => `${value}%`}
                              parser={(value) => value.replace("%", "")}
                            />
                          </Form.Item>
                        </>
                      )}

                      {selectedReloaningOption === "rollover" && (
                        <>
                          <Divider orientation="left" orientationMargin="0">
                            Rollover Settings
                          </Divider>

                          <Row gutter={16}>
                            <Col span={12}>
                              <Form.Item
                                label="Rollover Penalty Fee (%)"
                                name={[
                                  "reloaning_settings",
                                  "rollover_penalty_fee",
                                ]}
                                extra="Penalty fee for rolling over the loan"
                              >
                                <InputNumber
                                  min={0}
                                  max={25}
                                  style={{ width: "100%" }}
                                  placeholder="e.g., 5"
                                  formatter={(value) => `${value}%`}
                                  parser={(value) => value.replace("%", "")}
                                />
                              </Form.Item>
                            </Col>
                            <Col span={12}>
                              <Form.Item
                                label="Max Rollover Extensions"
                                name={[
                                  "reloaning_settings",
                                  "max_rollover_extensions",
                                ]}
                                extra="Maximum number of rollover extensions allowed"
                              >
                                <InputNumber
                                  min={1}
                                  max={3}
                                  style={{ width: "100%" }}
                                  placeholder="e.g., 2"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </>
                      )}

                      <Divider orientation="left" orientationMargin="0">
                        Automation Settings
                      </Divider>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label="Auto-approve Reloaning"
                            name={["reloaning_settings", "auto_approve"]}
                            valuePropName="checked"
                            extra="Automatically approve reloaning for eligible borrowers"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Require Manual Review"
                            name={[
                              "reloaning_settings",
                              "require_manual_review",
                            ]}
                            valuePropName="checked"
                            extra="Require manual review for high-risk reloaning cases"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        label="Risk Assessment Factors"
                        name={["reloaning_settings", "risk_factors"]}
                        extra="Select factors to consider for reloaning risk assessment"
                      >
                        <Select
                          mode="multiple"
                          placeholder="Select risk assessment factors"
                          options={[
                            {
                              label: "Payment History",
                              value: "payment_history",
                            },
                            {
                              label: "Current Debt-to-Income Ratio",
                              value: "debt_to_income",
                            },
                            {
                              label: "Employment Status",
                              value: "employment_status",
                            },
                            {
                              label: "Collateral Value",
                              value: "collateral_value",
                            },
                            {
                              label: "Credit Score Change",
                              value: "credit_score_change",
                            },
                            {
                              label: "Previous Reloaning History",
                              value: "previous_reloaning",
                            },
                          ]}
                        />
                      </Form.Item>

                      <Alert
                        message="Compliance Notice"
                        description="Ensure reloaning settings comply with local regulations including Fair Lending practices, Consumer Protection laws, and Risk Management standards (ISO 31000). Consider CRB guidelines for responsible lending."
                        type="warning"
                        showIcon
                        style={{ marginTop: 16 }}
                      />
                    </>
                  ) : null;
                }}
              </Form.Item>
            </Tabs.TabPane>
          </Tabs>

          <Divider />
          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              {currentProduct ? "Update Product" : "Create Loan Product"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <LoanProductCalculator products={products} />
    </div>
  );
};

export default LoanProductsPage;
